import { FormInstance } from "antd"
import { DocumentData } from "firebase/firestore"
import { find, keyBy, mapValues, uniq } from "lodash"
import { useMemo } from "react"

import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  CoreSkillLevel,
  DigitalSkillLevel,
  SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { EvaluationTrait } from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import {
  type AddStudentFormValues,
  StudentConstructor,
  StudentSkillHistory,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useSaveDoc } from "#hooks/jotai/useSaveDoc"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import { useFirebaseTransaction } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"
import {
  generateEvalId,
  getClampedEvalSemester,
  getNextSkill,
  getPrevEval,
} from "#utils/evaluationHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"
import { addNewSkillEntry } from "#utils/studentHelpers"
import { getSkillLevelFormValues } from "#utils/studentSkillHelpers"
import {
  createMissingGuardianInfoTask,
  hasGuardianContactInfo,
} from "#utils/taskHelpers"

import AddNewStudentForm from "./AddNewStudentForm"
import StudentProfile from "./StudentProfile"

export interface AddStudentFormModalProps {
  onClose: () => void
  studentId: Uuid | undefined
  /**
   * Fires with the saved doc id after a successful create or update, before
   * the modal closes. Lets a parent form (e.g. AddTryoutForm) select the
   * student it just created instead of relying on the Jotai subscription.
   */
  onSaved?: (studentId: Uuid) => void
}

type StudentSaveData = Partial<StudentConstructor> & { id?: Uuid }

/** Shown only while an existing student doc has not reached the atom yet. */
const EMPTY_ADD_STUDENT_FORM_VALUES: AddStudentFormValues = {
  id: undefined,
  firstName: undefined,
  lastName: undefined,
  name: undefined,
  avatar: undefined,
  dob: undefined,
  gender: undefined,
  coreSkillLevel: undefined,
  digitalSkillLevel: undefined,
  sculptureSkillLevel: undefined,
  skillHistory: undefined,
  primaryAddress: undefined,
  personalPhone: undefined,
  personalEmail: undefined,
  guardian1Name: undefined,
  guardian1Phone: undefined,
  guardian1Email: undefined,
  guardian2Name: undefined,
  guardian2Phone: undefined,
  guardian2Email: undefined,
  notes: undefined,
  tags: undefined,
  allergies: undefined,
}

/**
 * Fields the add/edit student forms register and that this modal has been
 * writing through to Firestore. Unregistered student fields (attendance,
 * artwork, files, …) are not in the submit payload.
 */
const copyPresentStudentFields = (
  values: AddStudentFormValues
): StudentSaveData => {
  const save: StudentSaveData = {}

  if (Object.hasOwn(values, "id")) save.id = values.id
  if (Object.hasOwn(values, "firstName")) save.firstName = values.firstName
  if (Object.hasOwn(values, "lastName")) save.lastName = values.lastName
  if (Object.hasOwn(values, "avatar")) save.avatar = values.avatar
  if (Object.hasOwn(values, "name")) save.name = values.name
  if (Object.hasOwn(values, "dob")) save.dob = values.dob
  if (Object.hasOwn(values, "gender")) save.gender = values.gender
  // The three skill-level fields are inputs, not stored fields — handleFinish
  // folds a touched one into skillHistory for the current semester.
  if (Object.hasOwn(values, "primaryAddress")) {
    save.primaryAddress = values.primaryAddress
  }
  if (Object.hasOwn(values, "personalPhone")) {
    save.personalPhone = values.personalPhone
  }
  if (Object.hasOwn(values, "personalEmail")) {
    save.personalEmail = values.personalEmail
  }
  if (Object.hasOwn(values, "guardian1Name")) {
    save.guardian1Name = values.guardian1Name
  }
  if (Object.hasOwn(values, "guardian1Phone")) {
    save.guardian1Phone = values.guardian1Phone
  }
  if (Object.hasOwn(values, "guardian1Email")) {
    save.guardian1Email = values.guardian1Email
  }
  if (Object.hasOwn(values, "guardian2Name")) {
    save.guardian2Name = values.guardian2Name
  }
  if (Object.hasOwn(values, "guardian2Phone")) {
    save.guardian2Phone = values.guardian2Phone
  }
  if (Object.hasOwn(values, "guardian2Email")) {
    save.guardian2Email = values.guardian2Email
  }
  if (Object.hasOwn(values, "notes")) save.notes = values.notes
  if (Object.hasOwn(values, "tags")) save.tags = values.tags
  if (Object.hasOwn(values, "allergies")) save.allergies = values.allergies

  if (typeof values.createdAt === "string") save.createdAt = values.createdAt
  if (typeof values.createdBy === "string") save.createdBy = values.createdBy
  if (typeof values.lastUpdatedAt === "string") {
    save.lastUpdatedAt = values.lastUpdatedAt
  }
  if (typeof values.lastUpdatedBy === "string") {
    save.lastUpdatedBy = values.lastUpdatedBy
  }

  return save
}

export const AddStudentFormModal = ({
  onClose,
  studentId,
  onSaved,
}: AddStudentFormModalProps) => {
  const isEdit = studentId != null
  const { trackStudentAction } = useAnalytics()
  const { currentUserId } = useUserContext()

  const { canCreate, canRead, canUpdate, canDelete } = useResourcePermissions(
    RESOURCES.STUDENT
  )
  const initialValues = useGetOneStudent((canRead && studentId) || NULL_ID)
  const formInitialValues = useMemo(
    () =>
      initialValues && {
        ...initialValues,
        ...getSkillLevelFormValues(initialValues),
      },
    [initialValues]
  )

  const updateEvalOnSkillChange = async (
    classGroup: CLASS_GROUPS,
    skillLevel: CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel,
    studentId: Uuid
  ) => {
    if (!canUpdate) {
      return
    }

    const evalId = generateEvalId(studentId, getCurrentSemester(), classGroup)
    const doesEvalExist = await Firebase.getInstance().checkIfDocExists(
      DOC_TYPES.EVALUATIONS,
      evalId
    )

    if (doesEvalExist) {
      let nextSkill = getNextSkill(skillLevel, classGroup)

      if (!nextSkill) {
        console.error(`Next skill for ${classGroup} ${skillLevel} is undefined`)
        // Default to current skill in case anything goes wrong
        nextSkill = skillLevel
      }

      const { prevEval, prevEvalTraitsById } = await getPrevEval(
        studentId,
        classGroup,
        getCurrentSemester()
      )

      const checkboxesQuery = {
        queries: {
          skillLevels: {
            value: skillLevel.replace("^", ""),
            comparator: COMPARATORS.CONTAINS,
          },
        },
      }
      const checkboxesFromDb =
        await Firebase.getInstance().variableQuery<EvaluationTrait>(
          DOC_TYPES.EVAL_TRAITS,
          checkboxesQuery
        )
      const checkboxesById = keyBy(
        checkboxesFromDb.filter((trait) =>
          trait.semesters.includes(getClampedEvalSemester(getCurrentSemester()))
        ),
        "id"
      )

      // Autofill checkboxes from previous eval
      const checkboxes = mapValues(checkboxesById, ({ name }) => {
        const previousCheckbox = find(prevEvalTraitsById, { name })
        if (!previousCheckbox || !prevEval) return false

        return prevEval.checkboxes[previousCheckbox.id]
      })

      await Firebase.getInstance().updateDocument(
        DOC_TYPES.EVALUATIONS,
        evalId,
        {
          currentSkill: skillLevel,
          nextSkill,
          checkboxes,
        }
      )
    }
  }

  const { saveDoc: saveStudent } = useSaveDoc<StudentConstructor>(
    DOC_TYPES.STUDENTS
  )

  // Errors are handled in GenericFormModal
  const handleFinish = async (
    cleanedValues: AddStudentFormValues,
    form?: FormInstance
  ): Promise<void> => {
    invariant(form != null, "Student form is missing!")

    if (!canCreate && !isEdit) {
      throw new Error("You do not have permission to create students")
    }
    if (!canUpdate && isEdit) {
      throw new Error("You do not have permission to update students")
    }

    const studentToSave = copyPresentStudentFields(cleanedValues)

    if (initialValues?.id != null) {
      studentToSave.id = initialValues.id
    }

    // Autofill name from first and last name. Matches Student/Person.getName
    // when the display name was left blank (cleanFormData turns "" into null).
    if (studentToSave.name == null) {
      studentToSave.name =
        `${cleanedValues.firstName} ${cleanedValues.lastName}`.trim()
    }

    const coreSkillLevel = cleanedValues.coreSkillLevel
    const digitalSkillLevel = cleanedValues.digitalSkillLevel
    const sculptureSkillLevel = cleanedValues.sculptureSkillLevel
    const hasNewCoreSkill =
      form.isFieldTouched("coreSkillLevel") && coreSkillLevel
    const hasNewDigitalSkill =
      form.isFieldTouched("digitalSkillLevel") && digitalSkillLevel
    const hasNewSculptureSkill =
      form.isFieldTouched("sculptureSkillLevel") && sculptureSkillLevel

    if (hasNewCoreSkill || hasNewDigitalSkill || hasNewSculptureSkill) {
      const newEntry: Partial<StudentSkillHistory[string]> = {}

      if (hasNewCoreSkill && coreSkillLevel) {
        newEntry[StudentSkillType.core] = coreSkillLevel

        if (isEdit) {
          invariant(
            initialValues?.id != null,
            `Data for _student_id ${studentId} is missing!`
          )
          await updateEvalOnSkillChange(
            CLASS_GROUPS.CORE,
            coreSkillLevel,
            initialValues.id
          )
        }
      }

      if (hasNewDigitalSkill && digitalSkillLevel) {
        newEntry[StudentSkillType.digital] = digitalSkillLevel

        if (isEdit) {
          invariant(
            initialValues?.id != null,
            `Data for _student_id ${studentId} is missing!`
          )
          await updateEvalOnSkillChange(
            CLASS_GROUPS.DIGITAL_ART,
            digitalSkillLevel,
            initialValues.id
          )
        }
      }

      if (hasNewSculptureSkill && sculptureSkillLevel) {
        newEntry[StudentSkillType.sculpture] = sculptureSkillLevel

        if (isEdit) {
          invariant(
            initialValues?.id != null,
            `Data for _student_id ${studentId} is missing!`
          )
          await updateEvalOnSkillChange(
            CLASS_GROUPS.SCULPTURE,
            sculptureSkillLevel,
            initialValues.id
          )
        }
      }

      studentToSave.skillHistory = addNewSkillEntry(
        initialValues?.skillHistory ?? {},
        getCurrentSemester(),
        newEntry
      )
    }

    // New students only require name + dob. If no guardian contact info was
    // provided, flag the front desk to collect it from the parent.
    if (!isEdit && !hasGuardianContactInfo(studentToSave)) {
      invariant(
        currentUserId != null,
        `Data for _user_id ${currentUserId} is missing!`
      )
      studentToSave.tasks = [createMissingGuardianInfoTask(currentUserId)]
    }

    const savedStudentId = await saveStudent(studentToSave)
    // useSaveDoc surfaces its own error modal and resolves null on failure;
    // don't report a save that didn't happen.
    if (savedStudentId == null) {
      throw new Error("Student was not saved. Please try again.")
    }
    onSaved?.(savedStudentId)
    trackStudentAction(
      isEdit
        ? ANALYTICS_EVENTS.STUDENT_UPDATED
        : ANALYTICS_EVENTS.STUDENT_ADDED,
      {
        studentId: savedStudentId,
        studentName:
          typeof cleanedValues.firstName === "string"
            ? cleanedValues.firstName
            : (studentToSave.name ?? ""),
      }
    )
  }

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!canDelete) {
      throw new Error("You do not have permission to delete students")
    }

    if (!initialValues?.id) return

    // Get classes from attendance
    const classIds = uniq([
      ...Object.keys(initialValues.attendance ?? {}),
      ...Object.keys(initialValues.assistantAttendance ?? {}),
    ])

    const classesPromise =
      Firebase.getInstance().getDocumentsByIds<ClassConstructor>(
        DOC_TYPES.CLASSES,
        classIds
      )

    const tryoutsPromise =
      Firebase.getInstance().variableQuery<TryoutConstructor>(
        DOC_TYPES.TRYOUTS,
        {
          queries: {
            studentId: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    const makeupsPromise =
      Firebase.getInstance().variableQuery<MakeupConstructor>(
        DOC_TYPES.MAKEUPS,
        {
          queries: {
            studentId: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    // TODO: Add evals
    const evalsPromise = Firebase.getInstance().variableQuery<DocumentData>(
      DOC_TYPES.EVALUATIONS,
      {
        queries: {
          studentId: {
            value: initialValues.id,
            comparator: COMPARATORS.EQUALS,
          },
        },
      }
    )

    const [classes, tryouts, makeups, evals] = await Promise.all([
      classesPromise,
      tryoutsPromise,
      makeupsPromise,
      evalsPromise,
    ])

    const errors: string[] = []

    if (classes.length > 0) {
      const message = `Please remove this student from the following classes before deleting them: 
      
${classes.map((c) => c.name).join("\n")}`
      errors.push(message)
    }

    if (tryouts.length > 0) {
      const message = `Please remove this student from the following tryouts before deleting them: 
      
${tryouts.map((t) => t.id).join("\n")}`
      errors.push(message)
    }

    if (makeups.length > 0) {
      const message = `Please remove this student from the following makeups before deleting them: 
      
${makeups.map((m) => m.id).join("\n")}`
      errors.push(message)
    }

    if (evals.length > 0) {
      const message = `Please remove this student from the following evals before deleting them: 
      
${evals.map((e) => e.id).join("\n")}`
      errors.push(message)
    }

    if (errors.length > 0) {
      throw new Error(errors.join("\n\n"))
    }

    await handleTransaction([
      {
        docType: DOC_TYPES.STUDENTS,
        id: initialValues.id,
        data: null,
        action: "delete",
      },
    ])

    trackStudentAction(ANALYTICS_EVENTS.STUDENT_DELETED, {
      studentId: initialValues.id,
      studentName: initialValues.firstName,
    })

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  return (
    <GenericFormModal<AddStudentFormValues, AddStudentFormValues>
      width={1400}
      onClose={onClose}
      title={isEdit ? "Student Profile" : "Add Student"}
      initialValues={formInitialValues}
      onFinish={handleFinish}
      resource={RESOURCES.STUDENT}
      onDelete={handleDelete}
    >
      {isEdit ? (
        <StudentProfile
          initialValues={formInitialValues ?? EMPTY_ADD_STUDENT_FORM_VALUES}
        />
      ) : (
        <AddNewStudentForm initialValues={formInitialValues} />
      )}
    </GenericFormModal>
  )
}

export default AddStudentFormModal
